import { Dispatch, SetStateAction } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Volume } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Switch from '~/shared/components/Switch';
import Typography from '~/shared/components/Typography';
import { toaster } from '~/shared/utils/toaster';

import {
  baseUrl,
  datafyErrorToast,
  isSwitchDisabled,
  isSwitchedOn,
  OptimisticSwitchType,
  optmisticSwitchingInterval,
  updateOptimisticVolumeSwitch,
} from './helpers';

export default function OptimisticSwitch({
  volume,
  optimisticVolumeSwitches,
  setOptimisticVolumeSwitches,
  showLabel,
}: {
  volume: Volume;
  optimisticVolumeSwitches?: OptimisticSwitchType;
  setOptimisticVolumeSwitches: Dispatch<SetStateAction<OptimisticSwitchType>>;
  showLabel?: boolean;
}) {
  const { volumeId } = volume;
  const api = useXFetch();

  const { mutateAsync: datafyVolume } = useMutation({
    mutationKey: ['datafy-volume'],
    mutationFn: async (volumeId: Volume['volumeId']) => {
      const res = await api<unknown>(`${baseUrl}/${volumeId}/datafy`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return res;
    },
    onError: () => datafyErrorToast(volumeId),
  });

  const { mutateAsync: unDatafyVolume } = useMutation({
    mutationKey: ['undatafy-volume'],
    mutationFn: async (volumeId: string) =>
      await api<unknown>(`${baseUrl}/${volumeId}/undatafy`, {
        method: 'POST',
      }),
    onError: () =>
      toaster({
        variant: 'error',
        title: 'Undatafication Error',
        message: `Volume ${volumeId} has failed to Undatafy.`,
        toastId: volumeId,
      }),
  });

  if (optimisticVolumeSwitches?.some((v) => v.volId === volumeId)) {
    setTimeout(() => {
      setOptimisticVolumeSwitches((prev) => prev.filter((sw) => sw.volId !== volumeId));
    }, optmisticSwitchingInterval);
  }

  async function onChange() {
    try {
      if (isSwitchedOn(volume)) {
        setOptimisticVolumeSwitches((prev) =>
          updateOptimisticVolumeSwitch(prev, volumeId!, false, Date.now())
        );
        await unDatafyVolume(volumeId!);
      } else {
        setOptimisticVolumeSwitches((prev) =>
          updateOptimisticVolumeSwitch(prev, volumeId!, true, Date.now())
        );
        await datafyVolume(volume.volumeId);
      }
    } catch (err) {
      console.log('err', err);
      setOptimisticVolumeSwitches((prev) => updateOptimisticVolumeSwitch(prev, volumeId!));
    }
  }

  const { date, value: optimisticValue } =
    optimisticVolumeSwitches?.find((v) => v?.volId === volumeId) || {};

  const isEnabled = !isSwitchDisabled(volume) && !date;
  const value = date ? optimisticValue : isSwitchedOn(volume);

  return (
    <div className="w-fit flex gap-[12px] items-center">
      {showLabel ? (
        <Typography variant="p3" color={isEnabled ? undefined : 'text-[#949EAC]'}>
          {volume.monitoring === 'Migrating' || optimisticValue
            ? 'Datafying'
            : value
              ? 'Datafied'
              : 'Undatafied'}
        </Typography>
      ) : null}
      <Switch
        checked={value}
        onChange={() => (isEnabled ? onChange() : null)}
        className="z-10"
        disabled={!isEnabled}
      />
    </div>
  );
}
